// =============================================================================
// Origin UI — src/components/LetterGenerationModal.tsx
// 3-step modal for generating HR letters from an employee profile.
// Step 1: Pick template. Step 2: Additional input (if required). Step 3: Review + generate.
// Default output is PDF. HR Admin can toggle to Word (editable) with a warning.
// =============================================================================

import { useState, useEffect } from 'react';
import { letterApi, LetterTemplateListItem, LetterTemplateDetail, AdditionalInputField } from '@/services/letterApi';
import styles from './LetterGenerationModal.module.css';

interface Props {
  employeeId: string;
  employeeName: string;
  officeName: string;
  isHR: boolean;
  onClose: () => void;
  onGenerated: () => void;
}

type Step = 'pick' | 'input' | 'review';
type OutputFormat = 'pdf' | 'docx';

export function LetterGenerationModal({ employeeId, employeeName, officeName, isHR, onClose, onGenerated }: Props) {
  const [step, setStep] = useState<Step>('pick');
  const [templates, setTemplates] = useState<LetterTemplateListItem[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<LetterTemplateDetail | null>(null);
  const [additionalFields, setAdditionalFields] = useState<AdditionalInputField[]>([]);
  const [additionalValues, setAdditionalValues] = useState<Record<string, string>>({});
  const [signatoryName, setSignatoryName] = useState('');
  const [signatoryOptions, setSignatoryOptions] = useState<{ name: string; title: string }[]>([]);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('pdf');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTemplates();
  }, [employeeId]);

  async function loadTemplates() {
    try {
      setLoading(true);
      const data = await letterApi.getAvailableTemplates(employeeId);
      setTemplates(data);

      // Load signatory options from the employee's office details
      try {
        const offices = await letterApi.getAllOfficeDetails();
        const officeDetail = offices.find(o => o.officeName === officeName);
        if (officeDetail) {
          const options: { name: string; title: string }[] = [];
          if (officeDetail.defaultSignatory1Name) {
            options.push({
              name: officeDetail.defaultSignatory1Name,
              title: officeDetail.defaultSignatory1Title || 'Head of Human Resources',
            });
          }
          if (officeDetail.defaultSignatory2Name) {
            options.push({
              name: officeDetail.defaultSignatory2Name,
              title: officeDetail.defaultSignatory2Title || 'Human Resources Generalist',
            });
          }
          setSignatoryOptions(options);
          // Default to first signatory
          if (options.length > 0) {
            setSignatoryName(options[0].name);
          }
        }
      } catch {
        // Office details fetch failed — signatory dropdown will fall back to template defaults
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }

  async function handleSelectTemplate(templateId: string) {
    try {
      setError('');
      setLoading(true);
      const detail = await letterApi.getTemplateDetail(templateId);
      setSelectedTemplate(detail);

      // Set default signatory from options (loaded from office details)
      // If template has a specific default, use that; otherwise keep the office default
      if (detail.signatoryDefault) {
        setSignatoryName(detail.signatoryDefault);
      } else if (signatoryOptions.length > 0 && !signatoryName) {
        setSignatoryName(signatoryOptions[0].name);
      }

      // Reset format to PDF for each new template selection
      setOutputFormat('pdf');

      // Parse additional input schema
      if (detail.requiresAdditionalInput && detail.additionalInputSchema) {
        try {
          const fields = JSON.parse(detail.additionalInputSchema) as AdditionalInputField[];
          setAdditionalFields(fields);
          // Initialise values
          const initial: Record<string, string> = {};
          fields.forEach(f => { initial[f.key] = ''; });
          setAdditionalValues(initial);
          setStep('input');
        } catch {
          // Schema parse error — skip to review
          setAdditionalFields([]);
          setStep('review');
        }
      } else {
        setAdditionalFields([]);
        setStep('review');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  }

  function handleInputNext() {
    // Validate required fields
    for (const field of additionalFields) {
      if (field.required && !additionalValues[field.key]?.trim()) {
        setError(`${field.label} is required`);
        return;
      }
    }
    setError('');
    setStep('review');
  }

  async function handleGenerate() {
    if (!selectedTemplate) return;
    try {
      setGenerating(true);
      setError('');

      const request = {
        additionalInputValues: Object.keys(additionalValues).length > 0 ? additionalValues : undefined,
        signatoryName: signatoryName || undefined,
        // signatoryTitle is NOT sent — backend resolves it from the selected name
      };

      await letterApi.generateLetter(selectedTemplate.letterTemplateId, employeeId, request, outputFormat);
      onGenerated();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to generate letter');
    } finally {
      setGenerating(false);
    }
  }

  function formatDate(value: string): string {
    if (!value) return '';
    const d = new Date(value);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>
              {step === 'pick' && 'Generate Letter'}
              {step === 'input' && selectedTemplate?.name}
              {step === 'review' && 'Review & Generate'}
            </h2>
            <div className={styles.subtitle}>For {employeeName} · {officeName}</div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Steps indicator */}
        <div className={styles.steps}>
          <div className={`${styles.step} ${step === 'pick' ? styles.stepActive : ''} ${step !== 'pick' ? styles.stepDone : ''}`}>
            <span className={styles.stepNum}>1</span> Select Template
          </div>
          <div className={styles.stepLine} />
          <div className={`${styles.step} ${step === 'input' ? styles.stepActive : ''} ${step === 'review' && additionalFields.length > 0 ? styles.stepDone : ''}`}>
            <span className={styles.stepNum}>2</span> Details
          </div>
          <div className={styles.stepLine} />
          <div className={`${styles.step} ${step === 'review' ? styles.stepActive : ''}`}>
            <span className={styles.stepNum}>3</span> Generate
          </div>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {error && <div className={styles.error}>{error}<button className={styles.errorDismiss} onClick={() => setError('')}>✕</button></div>}

          {/* Step 1: Pick template */}
          {step === 'pick' && (
            loading ? <div className={styles.loadingText}>Loading templates...</div> : (
              <div className={styles.templateGrid}>
                {templates.length === 0 && <div className={styles.emptyText}>No letter templates available for this employee's office.</div>}
                {templates.map(t => (
                  <button key={t.letterTemplateId} className={styles.templateCard} onClick={() => handleSelectTemplate(t.letterTemplateId)}>
                    <div className={styles.templateName}>{t.name}</div>
                    {t.description && <div className={styles.templateDesc}>{t.description}</div>}
                    <div className={styles.templateBadges}>
                      {t.jurisdiction && <span className={styles.jurisdictionBadge}>{t.jurisdiction === 'All' ? 'All Offices' : t.jurisdiction}</span>}
                      {t.includeSalary && <span className={styles.salaryBadge}>Contains Salary</span>}
                    </div>
                  </button>
                ))}
              </div>
            )
          )}

          {/* Step 2: Additional input */}
          {step === 'input' && (
            <div className={styles.inputForm}>
              <p className={styles.inputHint}>This template requires some additional information:</p>
              {additionalFields.map(field => (
                <div key={field.key} className={styles.formField}>
                  <label className={styles.formLabel}>
                    {field.label}{field.required && ' *'}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      className={styles.formTextarea}
                      value={additionalValues[field.key] || ''}
                      onChange={e => setAdditionalValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                      rows={3}
                    />
                  ) : field.type === 'date' ? (
                    <input
                      type="date"
                      className={styles.formInput}
                      value={additionalValues[field.key] || ''}
                      onChange={e => setAdditionalValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                    />
                  ) : (
                    <input
                      type="text"
                      className={styles.formInput}
                      value={additionalValues[field.key] || ''}
                      onChange={e => setAdditionalValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                    />
                  )}
                </div>
              ))}
              <div className={styles.formActions}>
                <button className={styles.backBtn} onClick={() => setStep('pick')}>← Back</button>
                <button className={styles.nextBtn} onClick={handleInputNext}>Next →</button>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 'review' && selectedTemplate && (
            <div className={styles.reviewPanel}>
              <div className={styles.reviewSection}>
                <div className={styles.reviewLabel}>Template</div>
                <div className={styles.reviewValue}>{selectedTemplate.name}</div>
              </div>
              <div className={styles.reviewSection}>
                <div className={styles.reviewLabel}>Employee</div>
                <div className={styles.reviewValue}>{employeeName}</div>
              </div>
              <div className={styles.reviewSection}>
                <div className={styles.reviewLabel}>Office</div>
                <div className={styles.reviewValue}>{officeName}</div>
              </div>
              {Object.entries(additionalValues).filter(([, v]) => v).map(([key, value]) => (
                <div key={key} className={styles.reviewSection}>
                  <div className={styles.reviewLabel}>{additionalFields.find(f => f.key === key)?.label || key}</div>
                  <div className={styles.reviewValue}>{key.toLowerCase().includes('date') ? formatDate(value) : value}</div>
                </div>
              ))}

              {/* Output format toggle — HR Admin only */}
              {isHR && (
                <>
                  <div className={styles.reviewDivider} />
                  <div className={styles.reviewSection}>
                    <div className={styles.reviewLabel}>Output Format</div>
                    <div className={styles.formatToggle}>
                      <button
                        className={`${styles.formatBtn} ${outputFormat === 'pdf' ? styles.formatBtnActive : ''}`}
                        onClick={() => setOutputFormat('pdf')}
                      >
                        PDF (recommended)
                      </button>
                      <button
                        className={`${styles.formatBtn} ${outputFormat === 'docx' ? styles.formatBtnActive : ''}`}
                        onClick={() => setOutputFormat('docx')}
                      >
                        Word (editable)
                      </button>
                    </div>
                  </div>
                  {outputFormat === 'docx' && (
                    <div className={styles.formatWarning}>
                      <strong>Word documents are not saved to Origin.</strong> This file will be downloaded to your device only. After making edits, please convert to PDF and upload to the employee's Documents tab.
                    </div>
                  )}
                </>
              )}

              {isHR && (
                <>
                  <div className={styles.reviewDivider} />
                  <div className={styles.reviewSection}>
                    <div className={styles.reviewLabel}>Signatory</div>
                    {signatoryOptions.length > 0 ? (
                      <select
                        className={styles.formInput}
                        value={signatoryName}
                        onChange={e => setSignatoryName(e.target.value)}
                      >
                        {signatoryOptions.map(opt => (
                          <option key={opt.name} value={opt.name}>
                            {opt.name} — {opt.title}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className={styles.formInput}
                        value={signatoryName}
                        onChange={e => setSignatoryName(e.target.value)}
                        placeholder="e.g. Shannon Naudé"
                      />
                    )}
                  </div>
                </>
              )}
              <div className={styles.formActions}>
                <button className={styles.backBtn} onClick={() => additionalFields.length > 0 ? setStep('input') : setStep('pick')}>← Back</button>
                <button className={styles.generateBtn} onClick={handleGenerate} disabled={generating}>
                  {generating ? 'Generating...' : outputFormat === 'pdf' ? '📄 Generate PDF' : '📝 Download Word'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
